import { useState } from 'react'
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import {
  useAdminCourses,
  useCreateCourse,
  useUpdateCourse,
  useDeleteCourse,
  Course,
  CreateCourseDTO,
  UpdateCourseDTO,
} from '@/services/admin/courses.service'
import { CourseFormDialog } from './CourseFormDialog'
import { LevelIcon } from '@/components/ui/LevelIcon'
import { useAuth } from '@/hooks/use-auth'
import { canManageCourseResource } from '@/types/auth'
import { getErrorMessage } from '@/utils/error'

const statusLabels = {
  draft: 'Rascunho',
  published: 'Publicado',
  archived: 'Arquivado',
  coming_soon: 'Em Breve',
}

const statusColors = {
  draft: 'secondary',
  published: 'default',
  archived: 'outline',
  coming_soon: 'secondary',
} as const

const typeLabels = {
  regular: 'Regular',
  masterclass: 'Masterclass',
  drop: 'Drop',
  workshop: 'Workshop',
  trail: 'Trilha',
}

const levelLabels = {
  beginner: 'Iniciante',
  intermediate: 'Intermediário',
  advanced: 'Avançado',
  expert: 'Expert',
}

export function CoursesManagement() {
  const { toast } = useToast()
  const { user } = useAuth()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCourse, setEditingCourse] = useState<Course | null>(null)
  const [deletingCourse, setDeletingCourse] = useState<Course | null>(null)

  const { data: courses, isLoading } = useAdminCourses()
  const createCourse = useCreateCourse()
  const updateCourse = useUpdateCourse()
  const deleteCourse = useDeleteCourse()

  const handleCreate = () => {
    setEditingCourse(null)
    setDialogOpen(true)
  }

  const handleEdit = (course: Course) => {
    setEditingCourse(course)
    setDialogOpen(true)
  }

  const handleDelete = (course: Course) => {
    setDeletingCourse(course)
  }

  const confirmDelete = async () => {
    if (!deletingCourse) return

    try {
      await deleteCourse.mutateAsync(deletingCourse.id)
      toast({
        title: 'Curso deletado',
        description: `O curso "${deletingCourse.title}" foi removido com sucesso.`,
      })
      setDeletingCourse(null)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao deletar curso',
        description: getErrorMessage(error, 'Ocorreu um erro ao deletar o curso.'),
      })
    }
  }

  const handleSubmit = async (data: CreateCourseDTO | UpdateCourseDTO) => {
    try {
      if (editingCourse) {
        await updateCourse.mutateAsync({
          id: editingCourse.id,
          data: data as UpdateCourseDTO,
        })
        toast({
          title: 'Curso atualizado',
          description: 'O curso foi atualizado com sucesso.',
        })
      } else {
        await createCourse.mutateAsync(data as CreateCourseDTO)
        toast({
          title: 'Curso criado',
          description: 'O curso foi criado com sucesso.',
        })
      }
      setDialogOpen(false)
      setEditingCourse(null)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: editingCourse ? 'Erro ao atualizar curso' : 'Erro ao criar curso',
        description: getErrorMessage(error, 'Ocorreu um erro ao processar a solicitação.'),
      })
      throw error
    }
  }

  return (
    <>
      <Card className="glass-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Gerenciamento de Cursos</CardTitle>
              <CardDescription>Crie, edite e gerencie os cursos da plataforma</CardDescription>
            </div>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Curso
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !courses || courses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum curso cadastrado.</p>
              <p className="text-sm mt-2">Clique em "Novo Curso" para começar.</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Nível</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Módulos</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {courses.map((course) => (
                    <TableRow key={course.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{course.title}</div>
                          <div className="text-sm text-muted-foreground">{course.slug}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{typeLabels[course.type]}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <LevelIcon level={course.level} />
                          <span className="text-sm text-muted-foreground">
                            {levelLabels[course.level]}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusColors[course.status]}>
                          {statusLabels[course.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {!course.companyId ? (
                          <Badge variant="default" className="bg-blue-600 hover:bg-blue-700">
                            Global
                          </Badge>
                        ) : course.company ? (
                          <span className="text-sm">{course.company.name}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {course.modules?.length || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {canManageCourseResource(user?.role, course.companyId) ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(course)}
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(course)}
                              title="Deletar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Somente leitura</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <CourseFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        course={editingCourse}
        onSubmit={handleSubmit}
        isSubmitting={createCourse.isPending || updateCourse.isPending}
      />

      <AlertDialog open={!!deletingCourse} onOpenChange={() => setDeletingCourse(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso irá deletar permanentemente o curso "
              {deletingCourse?.title}" e todos os seus módulos e episódios.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteCourse.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
